import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import employeeApi from '../services/employeeApi';
import './EmployeeDashboard.css';

function EmployeeDashboard() {
  const [employee, setEmployee] = useState(null);
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const [verifyRes, todayRes] = await Promise.all([
        employeeApi.get('/employee/verify'),
        employeeApi.get('/attendances/today')
      ]);
      setEmployee(verifyRes.data.employee);
      setToday(todayRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('employeeToken');
        navigate('/employee/login');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('employeeToken');
    if (!token) {
      navigate('/employee/login');
      return;
    }
    fetchData();
  }, [navigate, fetchData]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  const handlePunchIn = async () => {
    setActionLoading(true);
    try {
      await employeeApi.post('/attendances', { action_type: 'punch_in' });
      showMessage('success', 'Punch In recorded successfully.');
      const { data } = await employeeApi.get('/attendances/today');
      setToday(data);
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to punch in');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePunchOut = async () => {
    setActionLoading(true);
    try {
      await employeeApi.post('/attendances', { action_type: 'punch_out' });
      showMessage('success', 'Punch Out recorded successfully.');
      const { data } = await employeeApi.get('/attendances/today');
      setToday(data);
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to punch out');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('employeeToken');
    navigate('/employee/login');
  };

  if (loading) {
    return (
      <div className="employee-dashboard">
        <div className="employee-dashboard-inner"><p>Loading...</p></div>
      </div>
    );
  }

  // Support both new format { sessions, has_open_session } and legacy single-record format
  const sessions = Array.isArray(today?.sessions)
    ? today.sessions
    : today?.punch_in_time != null
      ? [{ punch_in_time: today.punch_in_time, punch_out_time: today.punch_out_time, working_hours: today.working_hours, id: 'today' }]
      : [];
  const hasOpenSession =
    today?.has_open_session === true ||
    (today?.punch_in_time != null && (today?.punch_out_time == null || today?.punch_out_time === undefined));
  const canPunchIn = !hasOpenSession;
  const canPunchOut = hasOpenSession;
  const todayDate = new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="employee-dashboard">
      <div className="employee-dashboard-inner">
        <div className="employee-dashboard-header">
          <h1>Employee Portal</h1>
          <button type="button" className="btn-logout" onClick={handleLogout}>Logout</button>
        </div>
        <div className="employee-dashboard-card">
          <h2 className="employee-name">{employee?.full_name}</h2>
          <dl className="employee-details">
            {employee?.employee_code && <><dt>Employee Code</dt><dd>{employee.employee_code}</dd></>}
            {employee?.company_name && <><dt>Company</dt><dd>{employee.company_name}</dd></>}
            {employee?.designation && <><dt>Designation</dt><dd>{employee.designation}</dd></>}
            {employee?.email && <><dt>Email</dt><dd>{employee.email}</dd></>}
            {employee?.phone && <><dt>Phone</dt><dd>{employee.phone}</dd></>}
            {employee?.joining_date && <><dt>Joining Date</dt><dd>{new Date(employee.joining_date).toLocaleDateString('en-IN')}</dd></>}
            <dt>Today</dt>
            <dd>{todayDate}</dd>
          </dl>
        </div>
        {message.text && (
          <div className={`employee-message employee-message--${message.type}`}>{message.text}</div>
        )}
        {hasOpenSession && (
          <div className="employee-message employee-message--info">
            You have an open session. Punch out to complete it, then you can punch in again.
          </div>
        )}
        <div className="punch-actions">
          <button
            type="button"
            className="btn-punch btn-punch-in"
            onClick={handlePunchIn}
            disabled={actionLoading || !canPunchIn}
            title={!canPunchIn ? 'Punch out first to start a new session' : ''}
          >
            Punch In
          </button>
          <button
            type="button"
            className="btn-punch btn-punch-out"
            onClick={handlePunchOut}
            disabled={actionLoading || !canPunchOut}
            title={!canPunchOut ? 'Punch in first' : ''}
          >
            Punch Out
          </button>
        </div>
        <div className="today-status">
          {sessions.length > 0 && (
            <>
              <p className="today-status-title">Today&apos;s sessions</p>
              <ul className="today-sessions-list">
                {sessions.map((s, i) => (
                  <li key={s.id || s._id || i}>
                    In: <strong>{s.punch_in_time || '—'}</strong>
                    {s.punch_out_time != null ? (
                      <> → Out: <strong>{s.punch_out_time}</strong> {s.working_hours != null && `(${s.working_hours} hrs)`}</>
                    ) : (
                      ' (open)'
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
        <Link to="/" className="back-link">← Back to Home</Link>
      </div>
    </div>
  );
}

export default EmployeeDashboard;
